import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { InvoiceFormData, STEPS, StepId } from '../types';

interface WizardState {
  currentStep: number;
  formData: InvoiceFormData;
  errors: Partial<Record<StepId, string>>;
  isSubmitting: boolean;
  isComplete: boolean;
}

type WizardAction =
  | { type: 'SET_STEP'; payload: number }
  | { type: 'UPDATE_FORM_DATA'; payload: Partial<InvoiceFormData> }
  | { type: 'SET_ERROR'; payload: { step: StepId; error: string } }
  | { type: 'CLEAR_ERROR'; payload: StepId }
  | { type: 'SET_SUBMITTING'; payload: boolean }
  | { type: 'COMPLETE' }
  | { type: 'RESET' }
  | { type: 'LOAD_DRAFT'; payload: InvoiceFormData };

const initialFormData: InvoiceFormData = {
  purchaseInfo: {},
  contact: {},
  fiscal: {
    rfc: '',
    razonSocial: '',
    regimenFiscal: '',
    usoCfdi: '',
    codigoPostal: '',
    metodoPago: '',
    formaPago: '',
  },
  attachments: {},
};

const initialState: WizardState = {
  currentStep: 0,
  formData: initialFormData,
  errors: {},
  isSubmitting: false,
  isComplete: false,
};

const STORAGE_KEY = 'billing-portal-draft';

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    case 'UPDATE_FORM_DATA':
      return {
        ...state,
        formData: { ...state.formData, ...action.payload },
      };
    case 'SET_ERROR':
      return {
        ...state,
        errors: { ...state.errors, [action.payload.step]: action.payload.error },
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        errors: { ...state.errors, [action.payload]: undefined },
      };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.payload };
    case 'COMPLETE':
      return { ...state, isComplete: true };
    case 'RESET':
      return { ...initialState, currentStep: 0 };
    case 'LOAD_DRAFT':
      return { ...state, formData: action.payload };
    default:
      return state;
  }
}

interface WizardContextType {
  state: WizardState;
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateFormData: (data: Partial<InvoiceFormData>) => void;
  setError: (step: StepId, error: string) => void;
  clearError: (step: StepId) => void;
  setSubmitting: (value: boolean) => void;
  complete: () => void;
  reset: () => void;
  currentStepId: StepId;
  progress: number;
  canGoNext: boolean;
}

const WizardContext = createContext<WizardContextType | null>(null);

export function WizardProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(wizardReducer, initialState);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        dispatch({ type: 'LOAD_DRAFT', payload: parsed });
      } catch (e) {
        console.error('Failed to load draft', e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state.formData));
  }, [state.formData]);

  const goToStep = useCallback((step: number) => {
    dispatch({ type: 'SET_STEP', payload: step });
  }, []);

  const nextStep = useCallback(() => {
    if (state.currentStep < STEPS.length) {
      dispatch({ type: 'SET_STEP', payload: state.currentStep + 1 });
    }
  }, [state.currentStep]);

  const prevStep = useCallback(() => {
    if (state.currentStep > 0) {
      dispatch({ type: 'SET_STEP', payload: state.currentStep - 1 });
    }
  }, [state.currentStep]);

  const updateFormData = useCallback((data: Partial<InvoiceFormData>) => {
    dispatch({ type: 'UPDATE_FORM_DATA', payload: data });
  }, []);

  const setError = useCallback((step: StepId, error: string) => {
    dispatch({ type: 'SET_ERROR', payload: { step, error } });
  }, []);

  const clearError = useCallback((step: StepId) => {
    dispatch({ type: 'CLEAR_ERROR', payload: step });
  }, []);

  const setSubmitting = useCallback((value: boolean) => {
    dispatch({ type: 'SET_SUBMITTING', payload: value });
  }, []);

  const complete = useCallback(() => {
    dispatch({ type: 'COMPLETE' });
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    dispatch({ type: 'RESET' });
  }, []);

  const currentStepId = STEPS[state.currentStep]?.id || 'welcome';
  const progress = ((state.currentStep) / (STEPS.length - 1)) * 100;

  const canGoNext = !state.errors[currentStepId as StepId];

  return (
    <WizardContext.Provider
      value={{
        state,
        goToStep,
        nextStep,
        prevStep,
        updateFormData,
        setError,
        clearError,
        setSubmitting,
        complete,
        reset,
        currentStepId,
        progress,
        canGoNext,
      }}
    >
      {children}
    </WizardContext.Provider>
  );
}

export function useWizard() {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within WizardProvider');
  }
  return context;
}
